import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import {
  Brain, FileText, Database, Cpu, Shield,
  Building2, GraduationCap, BookOpen,
  CheckCircle, ArrowRight, ChevronRight,
  Lock, Server, Users, Zap, Menu, X
} from 'lucide-react';
import './landing.css';
import dmilogolong from '../../images/DeepMedIQ-long.jpeg';

const TECH_PILLARS = [
  {
    icon: <Brain size={32} />,
    title: 'Agentic AI',
    desc: 'Multi-step reasoning agents that plan, retrieve, and synthesize — not single-shot responses. Our agents decompose complex clinical questions, traverse knowledge graphs, and orchestrate sub-tasks to return answers grounded in your institutional content.',
  },
  {
    icon: <FileText size={32} />,
    title: 'Document Intelligence & Knowledge Graphs',
    desc: 'We build semantic knowledge graphs from your clinical documents — mapping entities, relationships, and concepts across guidelines, protocols, and pathways. This graph-based understanding enables reasoning that goes far beyond keyword or vector search alone.',
  },
  {
    icon: <Database size={32} />,
    title: 'Retrieval-Augmented Generation',
    desc: 'Hybrid retrieval combines dense vector embeddings with structured graph traversal to surface the most relevant content before generation. Every response cites its exact source — eliminating hallucination and ensuring clinical traceability.',
  },
  {
    icon: <Cpu size={32} />,
    title: 'Custom Trained Models',
    desc: 'Domain-adapted models fine-tuned on clinical corpora, medical ontologies (SNOMED CT, ICD-10, LOINC), and your institutional terminology. Purpose-built for precision — not retrofitted from general-purpose consumer AI.',
  },
  {
    icon: <Shield size={32} />,
    title: 'Trust & Governance',
    desc: 'Every inference is logged, attributed, and auditable. Role-based access controls, content provenance tracking, and configurable guardrails ensure AI behaves within the boundaries your institution defines — with full transparency.',
  },
];

const HOW_IT_WORKS = [
  {
    n: '01',
    title: 'You provide your content',
    desc: 'Upload your clinical guidelines, protocols, educational materials, and institutional knowledge. We accept any format — PDFs, Word documents, structured databases, and more.',
  },
  {
    n: '02',
    title: 'We build your private knowledge base',
    desc: 'Your content is processed, indexed, and structured into a secure AI knowledge layer isolated entirely to your organization. No data is shared with other institutions or third parties.',
  },
  {
    n: '03',
    title: 'AI agents are configured for your workflows',
    desc: 'We layer agentic AI for clinical reasoning, decision support, and education delivery — tuned to your standards, terminology, and clinical context. Nothing generic.',
  },
  {
    n: '04',
    title: 'Your clinicians get a branded app',
    desc: 'A clinician-facing interface — branded to your institution — delivers authoritative answers drawn exclusively from your content, with citations back to your source documents.',
  },
];

const WHO_WE_SERVE = [
  {
    icon: <BookOpen size={28} />,
    title: 'Medical Societies',
    desc: 'Deploy your clinical guidelines and position statements as AI-powered decision support — instantly accessible to all your members at the point of care.',
    bullets: ['Guideline-based Q&A', 'Member-facing app', 'CME content delivery'],
  },
  {
    icon: <Building2 size={28} />,
    title: 'Health Systems',
    desc: 'Give clinicians instant answers drawn from your own protocols, formularies, and clinical pathways — reducing variability and improving adherence.',
    bullets: ['Protocol decision support', 'Formulary intelligence', 'Clinical pathway navigation'],
  },
  {
    icon: <GraduationCap size={28} />,
    title: 'Academic Institutions',
    desc: 'Deliver clinical education and reasoning support at scale, built on your curriculum and faculty-authored content — available to residents and students 24/7.',
    bullets: ['Curriculum-aligned Q&A', 'Case-based learning', 'Resident support tools'],
  },
];

const SECURITY_ITEMS = [
  'Your data never leaves your private deployment',
  'HIPAA Compliant — end-to-end, by design',
  'SOC 2 Type II Certified',
  'End-to-end encryption at rest and in transit',
  'Role-based access controls for all users',
  'Full audit logging for every clinical interaction',
];

function LandingPage() {
  const { loginWithRedirect, isAuthenticated } = useAuth0();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleSignIn = () => isAuthenticated
    ? navigate('/')
    : loginWithRedirect({ appState: { returnTo: '/' } });

  const handleDemo = () => isAuthenticated ? navigate('/') : navigate('/demo');

  const scrollTo = (id) => {
    setMobileMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="lp">

      {/* ── NAV ── */}
      <nav className={`lp-nav ${scrolled ? 'lp-nav--scrolled' : ''}`}>
        <div className="lp-nav__inner">
          <img src={dmilogolong} alt="DeepMedIQ" className="lp-nav__logo" />

          <div className="lp-nav__links">
            <button onClick={() => scrollTo('how-it-works')}>How It Works</button>
            <button onClick={() => scrollTo('technology')}>Technology</button>
            <button onClick={() => scrollTo('who-we-serve')}>Who We Serve</button>
            <button onClick={() => scrollTo('security')}>Security</button>
          </div>

          <div className="lp-nav__actions">
            {isAuthenticated
              ? <button className="lp-btn lp-btn--primary" onClick={handleSignIn}>Open App</button>
              : <>
                  <button className="lp-btn lp-btn--ghost" onClick={handleSignIn}>Sign In</button>
                  <button className="lp-btn lp-btn--primary" onClick={handleDemo}>Request Demo</button>
                </>
            }
          </div>

          <button
            className="lp-nav__hamburger"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="lp-nav__mobile">
            <button onClick={() => scrollTo('how-it-works')}>How It Works</button>
            <button onClick={() => scrollTo('technology')}>Technology</button>
            <button onClick={() => scrollTo('who-we-serve')}>Who We Serve</button>
            <button onClick={() => scrollTo('security')}>Security</button>
            <div className="lp-nav__mobile-actions">
              <button className="lp-btn lp-btn--ghost" onClick={handleSignIn}>Sign In</button>
              <button className="lp-btn lp-btn--primary" onClick={handleDemo}>Request Demo</button>
            </div>
          </div>
        )}
      </nav>

      {/* ── HERO ── */}
      <section className="lp-hero">
        <div className="lp-hero__row">
        <div className="lp-hero__content">
          <div className="lp-hero__badge">
            <Shield size={13} /> Trusted by leading medical societies
          </div>

          <h1 className="lp-hero__headline">
            Your Clinical Knowledge,<br />
            <span className="lp-hero__headline-accent">Delivered as AI</span>
          </h1>

          <p className="lp-hero__sub">
            You own the content. You control the deployment.<br />
            We power the intelligence.
          </p>

          <div className="lp-hero__actions">
            {isAuthenticated ? (
              <button className="lp-btn lp-btn--hero-primary" onClick={handleSignIn}>
                Open App <ArrowRight size={17} />
              </button>
            ) : (
              <>
                <button className="lp-btn lp-btn--hero-primary" onClick={handleDemo}>
                  Request a Demo <ArrowRight size={17} />
                </button>
                <button className="lp-btn lp-btn--hero-ghost" onClick={handleSignIn}>
                  Sign In
                </button>
              </>
            )}
          </div>

          <div className="lp-hero__trust-row">
            <span><Shield size={13} /> HIPAA Compliant</span>
            <span className="lp-hero__dot" />
            <span><Lock size={13} /> SOC 2 Type II</span>
            <span className="lp-hero__dot" />
            <span><Server size={13} /> Private Deployment</span>
          </div>
        </div>

        {/* Mock UI card */}
        <div className="lp-hero__visual" aria-hidden="true">
          <div className="lp-mock">
            <div className="lp-mock__header">
              <div className="lp-mock__dot" />
              <div className="lp-mock__dot" />
              <div className="lp-mock__dot" />
              <span className="lp-mock__title">DeepMedIQ — Your Institution</span>
            </div>
            <div className="lp-mock__body">
              <div className="lp-mock__query">
                <div className="lp-mock__query-label">Q</div>
                <div className="lp-mock__query-text">
                  What are the first-line treatment options for heart failure with reduced ejection fraction per our protocol?
                </div>
              </div>
              <div className="lp-mock__answer">
                <div className="lp-mock__answer-label">
                  <Brain size={14} /> DeepMedIQ
                </div>
                <div className="lp-mock__answer-text">
                  Based on your institution's Heart Failure Protocol (v2024), first-line therapy includes:
                </div>
                <div className="lp-mock__answer-items">
                  <div className="lp-mock__item"><CheckCircle size={13} /> ACE inhibitor or ARB (or ARNI if tolerated)</div>
                  <div className="lp-mock__item"><CheckCircle size={13} /> Beta-blocker (carvedilol, metoprolol succinate, or bisoprolol)</div>
                  <div className="lp-mock__item"><CheckCircle size={13} /> Mineralocorticoid receptor antagonist</div>
                  <div className="lp-mock__item"><CheckCircle size={13} /> SGLT2 inhibitor (dapagliflozin or empagliflozin)</div>
                </div>
                <div className="lp-mock__citations">
                  <span className="lp-mock__cite">[1] HF Protocol 2024, p. 12</span>
                  <span className="lp-mock__cite">[2] ACC/AHA Guidelines 2022</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>{/* end lp-hero__row */}
      </section>

      {/* ── VALUE PROPOSITION ── */}
      <section className="lp-value">
        <div className="lp-container">
          <p className="lp-value__lead">
            Clinical knowledge exists everywhere. Getting it to clinicians — from the right source,
            at the right moment, with institutional authority — has always been the challenge.
            DeepMedIQ solves that, on your terms.
          </p>
          <div className="lp-value__grid">
            {WHO_WE_SERVE.map(({ icon, title, desc, bullets }) => (
              <div className="lp-value-card" key={title}>
                <div className="lp-value-card__icon">{icon}</div>
                <h3>{title}</h3>
                <p>{desc}</p>
                <ul>
                  {bullets.map(b => (
                    <li key={b}><ChevronRight size={14} />{b}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="lp-how" id="how-it-works">
        <div className="lp-container">
          <div className="lp-section-label">How It Works</div>
          <h2 className="lp-section-title">From your content to your clinicians in four steps</h2>
          <div className="lp-steps">
            {HOW_IT_WORKS.map(({ n, title, desc }, i) => (
              <div className="lp-step" key={n}>
                <div className="lp-step__left">
                  <div className="lp-step__num">{n}</div>
                  {i < HOW_IT_WORKS.length - 1 && <div className="lp-step__line" />}
                </div>
                <div className="lp-step__content">
                  <h3>{title}</h3>
                  <p>{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TECHNOLOGY ── */}
      <section className="lp-tech" id="technology">
        <div className="lp-container">
          <div className="lp-section-label lp-section-label--light">Why DeepMedIQ</div>
          <h2 className="lp-section-title lp-section-title--light">
            Cutting-edge AI, purpose-built for clinical trust
          </h2>
          <p className="lp-tech__sub">
            We combine five foundational technologies into a unified platform — so institutions
            get institutional-grade AI, not a consumer chatbot with a medical skin.
          </p>
          <div className="lp-tech__grid">
            {TECH_PILLARS.map(({ icon, title, desc }) => (
              <div className="lp-tech-card" key={title}>
                <div className="lp-tech-card__icon">{icon}</div>
                <h3>{title}</h3>
                <p>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHO WE SERVE ── */}
      <section className="lp-serve" id="who-we-serve">
        <div className="lp-container">
          <div className="lp-section-label">Who We Serve</div>
          <h2 className="lp-section-title">Built for institutions that set the standard</h2>
          <div className="lp-serve__grid">
            {WHO_WE_SERVE.map(({ icon, title, desc, bullets }) => (
              <div className="lp-serve-card" key={title}>
                <div className="lp-serve-card__icon">{icon}</div>
                <h3>{title}</h3>
                <p>{desc}</p>
                <ul>
                  {bullets.map(b => (
                    <li key={b}><CheckCircle size={14} />{b}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECURITY ── */}
      <section className="lp-security" id="security">
        <div className="lp-container lp-security__inner">
          <div className="lp-security__text">
            <div className="lp-section-label">Security & Compliance</div>
            <h2 className="lp-section-title">Built for institutional trust from day one</h2>
            <p>
              Unlike general-purpose AI tools, DeepMedIQ never aggregates or shares your
              content across institutions. Every deployment is private, governed, and fully
              under your control. Your data is yours — period.
            </p>
            <ul className="lp-security__list">
              {SECURITY_ITEMS.map(item => (
                <li key={item}><CheckCircle size={16} /><span>{item}</span></li>
              ))}
            </ul>
          </div>

          <div className="lp-security__badges">
            {[
              { icon: <Shield size={36} />, label: 'HIPAA', sub: 'Compliant' },
              { icon: <Lock size={36} />, label: 'SOC 2', sub: 'Type II Certified' },
              { icon: <Server size={36} />, label: 'Private', sub: 'Deployment' },
              { icon: <Zap size={36} />, label: 'Zero', sub: 'Data Sharing' },
            ].map(({ icon, label, sub }) => (
              <div className="lp-badge" key={label}>
                <div className="lp-badge__icon">{icon}</div>
                <strong>{label}</strong>
                <span>{sub}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="lp-cta">
        <div className="lp-container lp-cta__inner">
          <h2>Ready to deploy AI on your terms?</h2>
          <p>
            Join leading medical societies, health systems, and academic institutions
            using DeepMedIQ to bring their clinical knowledge to life.
          </p>
          <div className="lp-cta__actions">
            <button className="lp-btn lp-btn--hero-primary" onClick={handleDemo}>
              Request a Demo <ArrowRight size={17} />
            </button>
            <a href="mailto:info@deepmediq.ai" className="lp-btn lp-btn--hero-ghost">
              Contact Us
            </a>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="lp-footer">
        <div className="lp-container lp-footer__inner">
          <div className="lp-footer__brand">
            <img src={dmilogolong} alt="DeepMedIQ" className="lp-footer__logo" />
            <p>Private Clinical AI for Medical Institutions</p>
            <p>Bellevue, WA &nbsp;·&nbsp; <a href="mailto:info@deepmediq.ai">info@deepmediq.ai</a></p>
          </div>
          <div className="lp-footer__links">
            <button onClick={() => scrollTo('how-it-works')}>How It Works</button>
            <button onClick={() => scrollTo('technology')}>Technology</button>
            <button onClick={() => scrollTo('who-we-serve')}>Who We Serve</button>
            <button onClick={() => scrollTo('security')}>Security</button>
            <a href="mailto:info@deepmediq.ai">Contact</a>
          </div>
        </div>
        <div className="lp-footer__bottom">
          <div className="lp-container">
            <span>© {new Date().getFullYear()} DeepMedIQ. All rights reserved.</span>
            <span>You own the content. We power the intelligence.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
